import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import { useSettings } from "../../state/settings";
import { useUi } from "../../state/ui";
import { downloadSingle } from "../../files/download";
import { uploadLargeFile } from "../../files/upload";
import { convertFilesForUpload } from "../../files/convert";
import { uploadInBatches } from "../../convert/chunker";
import { toast } from "../../notify";
import { texte } from "../../texte";
import { FileView } from "../parts/FileView";

/** Kontrolldateien, die weder Werkzeug noch Mensch löschen darf (Server-seitig
 * ebenso durchgesetzt in plugins/botagent.py:_delete — dies hier ist nur der
 * UI-Vorfilter, damit der Button für diese drei gar nicht erst erscheint). */
const UNDELETABLE = new Set(["manifest", "goal", "agent_status"]);

/** Dateien-Tab: Liste mit Metadaten, Download/Löschen, Upload-Box — und bei
 * Auswahl einer Datei (Liste oder Rail-Tree via openFile) die Vorschau
 * (FileView) mit Zurück-Navigation zur Liste. Löschen ist für jede Rolle/Sicht
 * erlaubt (auch Einfach/Lernende) — nur die drei Kontrolldateien in UNDELETABLE
 * sind ausgenommen. Die Upload-Box bleibt Admin/Lehrkraft vorbehalten. */
export function Files({ ctx, isAdmin }: { ctx: ApiCtx; isAdmin: boolean }) {
  const s3Prefix = useSettings((s) => s.s3Prefix);
  const openDoc = useUi((s) => s.openDoc);
  const openFile = useUi((s) => s.openFile);
  const queryClient = useQueryClient();
  const queryKey = ["files", ctx.apiUrl, ctx.token, ctx.workspace];
  const query = useQuery({ queryKey, queryFn: () => api.files(ctx) });
  const docs = query.data?.documents ?? [];

  const [busy, setBusy] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function invalidateFiles() {
    queryClient.invalidateQueries({ queryKey });
  }

  async function handleDownload(docId: string) {
    try {
      await downloadSingle(ctx, docId);
      toast(texte.fileView.downloaded, texte.fileView.downloadedText, true);
    } catch (e) {
      console.error("[Dateien] Download fehlgeschlagen", docId, e);
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete(docId: string) {
    if (UNDELETABLE.has(docId)) return;
    if (!window.confirm(texte.files.deleteConfirm(docId))) return;
    setDeletingId(docId);
    try {
      const res = await api.deleteDoc(ctx, docId);
      if (res.code < 0) {
        console.error("[Dateien] Löschen fehlgeschlagen", docId, res.msg);
        toast(texte.errors.generic, res.msg ?? texte.errors.generic);
        return;
      }
      invalidateFiles();
      toast(texte.files.deleted, texte.files.deletedText(docId), true);
    } catch (e) {
      console.error("[Dateien] Löschen fehlgeschlagen", docId, e);
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleUpload(list: FileList | File[]) {
    const files = Array.from(list);
    if (!files.length) return;
    console.info("[Dateien] Upload gestartet", files.map((f) => f.name));
    setBusy(texte.files.uploading);
    try {
      const { smallDocs, smallImages, largeFiles } = await convertFilesForUpload(files, false);
      const { errors } = await uploadInBatches(ctx, smallDocs, smallImages, (p) =>
        setBusy(`${texte.files.uploading} (${p.batch}/${p.totalBatches})`),
      );
      for (const file of largeFiles) {
        setBusy(`${texte.files.uploading} ${file.name}…`);
        await uploadLargeFile(s3Prefix, ctx.workspace, file);
      }
      if (errors.length) {
        console.error("[Dateien] Upload-Fehler", errors);
        toast(texte.errors.generic, `${errors.length} Dokument(e) fehlgeschlagen: ${errors.map((e) => e.doc_id).join(", ")}`);
      } else {
        toast(texte.files.uploaded, texte.files.uploadedText(files.length), true);
      }
      invalidateFiles();
    } catch (e) {
      console.error("[Dateien] Upload fehlgeschlagen", e);
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (openDoc) {
    const meta = docs.find((d) => d.doc_id === openDoc);
    return <FileView ctx={ctx} docId={openDoc} meta={meta} />;
  }

  return (
    <>
      {isAdmin && (
        <>
          <div
            className={`dropzone${dragOver ? " over" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files);
            }}
          >
            {busy ?? texte.files.dropzoneText}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
        </>
      )}
      {docs.length === 0 ? (
        <div className="empty" style={{ marginTop: 16, maxWidth: 560 }}>
          {texte.files.empty}
        </div>
      ) : (
        <div className="files-table" style={{ marginTop: isAdmin ? 18 : 0 }}>
          {docs.map((d) => (
            <div
              className={`frow${openDoc === d.doc_id ? " active" : ""}`}
              key={d.doc_id}
              role="button"
              tabIndex={0}
              onClick={() => openFile(d.doc_id)}
              onKeyDown={(e) => e.key === "Enter" && openFile(d.doc_id)}
            >
              <span className="ico">📄</span>
              <span className="name">{d.doc_id}</span>
              <span className="meta">{d.summary}</span>
              <span className="meta">{d.updated_at}</span>
              <span className="fa">
                <button
                  className="btn ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(d.doc_id);
                  }}
                >
                  {texte.fileView.download}
                </button>
                {!UNDELETABLE.has(d.doc_id) && (
                  <button
                    className="btn ghost"
                    disabled={deletingId === d.doc_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(d.doc_id);
                    }}
                  >
                    {texte.files.delete}
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
